import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { WishlistProvider } from "./lib/wishlist";
import { ShortlistProvider } from "./lib/shortlist";
import { trackSiteVisit } from "./lib/analytics";
import { detectGeoOnce } from "./lib/geo";
import { Toaster } from "./components/ui/sonner";
import { installAntiClone } from "./lib/antiClone";
import { installSafeAreaShim } from "./lib/native";
import { initAdMob, showHomeBanner } from "./lib/admob";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import Home from "./pages/Home";
import Browse from "./pages/Browse";
import ListingDetail from "./pages/ListingDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import CountryLanding from "./pages/CountryLanding";
import SignInGate from "./components/SignInGate";
// v79.173 — BackButton retired (returns null); kept import to preserve
// module load order but no longer rendered.
import BackButton from "./components/BackButton"; // eslint-disable-line no-unused-vars

// Lazy-load every non-critical route so the initial bundle stays slim.
// First paint only needs Home/Browse/Listing/Login/Register; the rest stream
// in on demand, dropping the JS download cost for first-time visitors.
const PostAd = lazy(() => import("./pages/PostAd"));
const MyListings = lazy(() => import("./pages/MyListings"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Messages = lazy(() => import("./pages/Messages"));
const Admin = lazy(() => import("./pages/Admin"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const Legal = lazy(() => import("./pages/Legal"));
const Verify = lazy(() => import("./pages/Verify"));
const Shop = lazy(() => import("./pages/Shop"));
// v79.98 — Funboard (Flipboard-by-Adukoo). Full-screen vertical swipe
// feed of curated memes / videos / news + interleaved Adukoo listings
// and Digital Stores. Age-gated at 16+ by the /funboard route.
const Funboard = lazy(() => import("./pages/Funboard"));
const FunboardBuzz = lazy(() => import("./pages/FunboardBuzz"));
const FunboardGang = lazy(() => import("./pages/FunboardGang"));
const FunboardReels = lazy(() => import("./pages/FunboardReels"));
const FunboardSaved = lazy(() => import("./pages/FunboardSaved"));
const ForumList = lazy(() => import("./pages/Forum").then((m) => ({ default: m.ForumList })));
const ForumThread = lazy(() => import("./pages/Forum").then((m) => ({ default: m.ForumThread })));
const FunboardShareIn = lazy(() => import("./pages/FunboardShareIn"));
const ShopEdit = lazy(() => import("./pages/ShopEdit"));
const ShopsDirectory = lazy(() => import("./pages/ShopsDirectory"));
const MyBusinesses = lazy(() => import("./pages/MyBusinesses"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const AccountSecurity = lazy(() => import("./pages/AccountSecurity"));
const CreatorProfile = lazy(() => import("./pages/CreatorProfile"));
const CreatorDashboard = lazy(() => import("./pages/CreatorDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Guides = lazy(() => import("./pages/Guides"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));
const ContactRequests = lazy(() => import("./pages/ContactRequests"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const HelpDigitalStores = lazy(() => import("./pages/HelpDigitalStores"));
const UserGuide = lazy(() => import("./pages/UserGuide"));
// v78.48 — Depth pages for Google AdSense compliance (About / Terms / FAQ).
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Investors = lazy(() => import("./pages/Investors"));
const EmployerApply = lazy(() => import("./pages/EmployerApply"));
const Candidates = lazy(() => import("./pages/Candidates"));
const EmployerShortlist = lazy(() => import("./pages/EmployerShortlist"));
// v78.55 — Auth Wall (Concept K). Single page handling sign-up + sign-in;
// becomes the only entry for unauthenticated users hitting gated routes.
const AuthWall = lazy(() => import("./pages/AuthWall"));
// v79.151 — Mandatory security-questions setup screen for brand-new
// signups. Reduces reliance on the (slow) admin-ticket recovery path.
const SecuritySetup = lazy(() => import("./pages/SecuritySetup"));
// v79.82 — Managed clients (B2B/agency workflow: post on behalf of a brand)
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const ClientAnalyticsPage = lazy(() => import("./pages/ClientAnalyticsPage"));
// v79.104 — Adukoo Helper chatbot (free, rule-based, multi-language)
import HelperBot from "./components/HelperBot";
// v79.114 — Share-to-Funboard FAB, available on every page for signed-in users
import FunboardShareFabGlobal from "./components/FunboardShareFabGlobal";
// v79.118 — Global in-app media player mount
import GlobalMediaPlayer from "./components/GlobalMediaPlayer";

function Protected({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <div className="p-10 text-center text-aduku-navy/60">Loading...</div>;
    if (!user) {
        // v78.55 — Send guests to the new /auth wall and remember where they
        // were headed so we can bounce them back post-login.
        const next = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/auth?next=${next}`} replace />;
    }
    return children;
}

// v78.55 — Hide the global Header on the full-bleed /auth wall so the
// cinematic background and form panel own the entire viewport. Footer
// stays hidden on /auth for the same reason.
// v79.98 — Funboard is a full-screen Reels-style surface; hiding the
// Marketplace chrome removes the white gap above the black feed and
// lets the swipe UX truly own the viewport.
function ChromeHeader() {
    const location = useLocation();
    if (location.pathname.startsWith("/auth")) return null;
    // Hide header only on the full-screen swipe feed (/funboard), but
    // keep it on /funboard/saved and other Funboard sub-pages so users
    // can navigate back and search.
    // v79.109 — Funboard is now a list feed at /funboard (Header stays
    // visible for search + nav); only the immersive /funboard/reels
    // route hides the site header and footer for full-screen viewing.
    if (location.pathname === "/funboard/reels") return null;
    return <Header />;
}
function ChromeFooter() {
    const location = useLocation();
    if (location.pathname.startsWith("/auth")) return null;
    if (location.pathname === "/funboard/reels") return null;
    return <Footer />;
}

/** v79.48 — Preserve query params (?next=…) across the /login and
 *  /register → /auth redirects. Founder direction: the header "+"
 *  post-menu links must survive the auth wall so the user lands on the
 *  correct posting flow after signup. */
function AuthRedirect({ mode }) {
    const location = useLocation();
    const search = new URLSearchParams(location.search);
    search.set("mode", mode);
    return <Navigate to={`/auth?${search.toString()}`} replace />;
}

/** v78.17 — Manual scroll restoration with retry.
 *
 * Founder report: pressing Back from an ad lands at the FOOTER instead
 * of the rail they came from. Root cause is a mismatch between the
 * browser's native restore (fires immediately on POP) and our home /
 * browse pages (which lazy-load category rails over the next ~1.5 s).
 * The browser tries to scroll to Y=1800 but only 1443 px of content
 * has rendered, so it snaps to the bottom and stays there.
 *
 * Fix:
 *  1. Disable the browser's native scrollRestoration (it's broken for
 *     lazy-loaded pages).
 *  2. Continuously persist `window.scrollY` to sessionStorage keyed by
 *     the React Router `location.key` (debounced — at most every 150 ms).
 *  3. On POP navigation back to a saved key, re-apply that scroll Y
 *     position EIGHT TIMES at 200 ms intervals — each attempt nudges
 *     the page closer as lazy content fills in. The retry loop ends
 *     either when the requested Y is reachable or after 1.6 s.
 *  4. On PUSH / REPLACE navigation, behave like the old ScrollToTop
 *     and snap to the top of the new route.
 */
function ScrollManager() {
    const location = useLocation();
    const navType = useNavigationType();

    // 1) Take over scroll restoration once, on mount.
    useEffect(() => {
        try {
            if ("scrollRestoration" in window.history) {
                window.history.scrollRestoration = "manual";
            }
        } catch { /* SSR / old browsers */ }
    }, []);

    // 2) Persist scrollY of the CURRENT location key, debounced.
    useEffect(() => {
        const key = `aduku_scroll:${location.key || "default"}`;
        let t = null;
        const save = () => {
            try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* */ }
        };
        const onScroll = () => {
            if (t) clearTimeout(t);
            t = setTimeout(save, 150);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (t) clearTimeout(t);
            save(); // capture one last position before unmount
        };
    }, [location.key]);

    // 3) Restore on POP, otherwise scroll to top.
    useEffect(() => {
        if (navType === "POP") {
            const key = `aduku_scroll:${location.key || "default"}`;
            let saved = null;
            try { saved = sessionStorage.getItem(key); } catch { /* */ }
            const targetY = saved ? parseInt(saved, 10) : NaN;
            if (Number.isFinite(targetY) && targetY > 0) {
                // Retry up to 8 times (1.6 s window) to outwait lazy-loaded
                // rails / images — each attempt re-snaps to the saved Y
                // once more content has rendered and the document is tall
                // enough to reach it.
                let remaining = 8;
                const tryScroll = () => {
                    window.scrollTo(0, targetY);
                    if (--remaining > 0) setTimeout(tryScroll, 200);
                };
                tryScroll();
                return;
            }
        }
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [location.key, navType]);

    return null;
}

// v78.60/.61 — /catalogs side-effect lives inside a mounted component so it
// only fires once on route entry (not on every App render). Public HTML hub
// lives in /public/marketing-assets/catalogs/index.html and is shipped as a
// static asset, so we hard-redirect to it via window.location.replace.
function CatalogRedirect() {
    useEffect(() => {
        try {
            window.location.replace("/marketing-assets/catalogs/index.html");
        } catch { /* no-op: SSR or sandboxed env */ }
    }, []);
    return (
        <div className="p-10 text-center text-aduku-navy/60" data-testid="catalog-redirect">
            Opening catalogs hub…
        </div>
    );
}

export default function App() {
    useEffect(() => {
        installAntiClone(); installSafeAreaShim(); trackSiteVisit(); detectGeoOnce();
        // v79.115 — Android app is portrait-only. Runtime lock via the
        // Screen Orientation API works inside the Capacitor WebView.
        // For a HARD lock at the Android level (fires before the WebView
        // paints), the AndroidManifest.xml MainActivity must also carry
        // `android:screenOrientation="portrait"` — see
        // /app/memory/ANDROID_PORTRAIT_LOCK.md for the exact patch.
        try {
            if (typeof navigator !== "undefined" && navigator.userAgent && navigator.userAgent.includes("AdukooApp")) {
                if (window.screen?.orientation?.lock) {
                    window.screen.orientation.lock("portrait").catch(() => { /* browser blocked — no-op */ });
                }
            }
        } catch { /* ignore */ }
        // Boot AdMob on the Android app (no-op on web). Banner shows once
        // the SDK is ready; consent form (UMP) appears first for EEA users.
        initAdMob().then((ok) => { if (ok) showHomeBanner(); });
        // Tell the splash overlay in index.html that React has mounted and
        // painted — the listener dismisses the splash with a fade.
        try { window.dispatchEvent(new Event("adukoo:ready")); } catch { /* SSR / old browser no-op */ }
    }, []);
    return (
        <div className="App aduku-watermark">
            <ErrorBoundary>
            <BrowserRouter>
                <ScrollManager />
                <AuthProvider>
                    <WishlistProvider>
                        <ShortlistProvider>
                        <ChromeHeader />
                        {/* v78.59 — Global "please sign in" modal mounted once
                            so any gated action (Save, Subscribe, Chat, Show
                            phone) can call `await requireAuth(reason)` and
                            keep the user on the page instead of yanking them
                            to /auth. See components/SignInGate.jsx. */}
                        <SignInGate />
                        <main className="min-h-[60vh] relative">
                            {/* v79.173 — Founder direction: "remove back
                                buttons on every page. let customers use
                                back button in browser in lap, back button
                                mobile, tab. Only action buttons all
                                pages in admin side and user side too."
                                Global BackButton retired — the component
                                itself was updated to return null too, so
                                any remaining imports are dead but safe. */}
                            <Suspense fallback={<div className="p-10 text-center text-aduku-navy/60">Loading…</div>}>
                                <Routes>
                                {/* v78.58 — Funnel fix (P0). The homepage,
                                    Browse, and the Digital Stores directory
                                    were behind <Protected>, so every new
                                    visitor was bounced to /auth before seeing
                                    a single listing or store. Live data:
                                    18.7k visits → 79 signups (0.42%
                                    conversion vs. 2-5% marketplace
                                    benchmark). Un-gating these three
                                    discovery surfaces lets guests browse
                                    freely; we still ask them to sign in the
                                    moment they take a gated action (post,
                                    message, save, subscribe) via inline
                                    redirects on the action buttons. */}
                                <Route path="/" element={<Home />} />
                                {/* v79.174 — Funboard split. `/funboard`
                                    redirects to `/funboard/gang` (founder
                                    default landing). Buzz and Gang each
                                    live at their own route and behave as
                                    independent apps. */}
                                <Route path="/funboard" element={<Navigate to="/funboard/gang" replace />} />
                                <Route path="/funboard/gang" element={<FunboardGang />} />
                                <Route path="/funboard/buzz" element={<FunboardBuzz />} />
                                <Route path="/funboard/reels" element={<FunboardReels />} />
                                <Route path="/funboard/saved" element={<FunboardSaved />} />
                                <Route path="/funboard/share" element={<FunboardShareIn />} />
                                {/* v79.181 — Community Forum (anonymous OK) */}
                                <Route path="/forum" element={<ForumList />} />
                                <Route path="/forum/:id" element={<ForumThread />} />
                                <Route path="/investors" element={<Investors />} />
                                <Route path="/c/:country" element={<CountryLanding />} />
                                <Route path="/browse" element={<Browse />} />
                                <Route path="/listing/:id" element={<ListingDetail />} />
                                <Route path="/post" element={<Protected><PostAd /></Protected>} />
                                <Route path="/listing/:id/edit" element={<Protected><PostAd /></Protected>} />
                                <Route path="/my-listings" element={<Protected><MyListings /></Protected>} />
                                <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
                                <Route path="/messages" element={<Protected><Messages /></Protected>} />
                                <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
                                <Route path="/wishlist" element={<Protected><Wishlist /></Protected>} />
                                <Route path="/contact-requests" element={<Protected><ContactRequests /></Protected>} />
                                <Route path="/profile/clients" element={<Protected><ClientsPage /></Protected>} />
                                <Route path="/profile/clients/:id" element={<Protected><ClientAnalyticsPage /></Protected>} />
                                <Route path="/account/verify" element={<Protected><Verify /></Protected>} />
                                <Route path="/shops/me/edit" element={<Protected><ShopEdit /></Protected>} />
                                <Route path="/businesses/mine" element={<Protected><MyBusinesses /></Protected>} />
                                <Route path="/digital-stores/me/edit" element={<Protected><ShopEdit /></Protected>} />
                                <Route path="/digital-stores/edit/:shopId" element={<Protected><ShopEdit /></Protected>} />
                                <Route path="/digital-stores/mine" element={<Protected><MyBusinesses /></Protected>} />
                                <Route path="/digital-stores" element={<ShopsDirectory />} />
                                <Route path="/digital-stores/:slug" element={<Shop />} />
                                <Route path="/shops" element={<Navigate to="/digital-stores" replace />} />
                                <Route path="/shop/:slug" element={<Shop />} />
                                <Route path="/auth" element={<AuthWall />} />
                                <Route path="/auth/callback" element={<AuthCallback />} />
                                <Route path="/security-setup" element={<Protected><SecuritySetup /></Protected>} />
                                <Route path="/admin" element={<Protected><Admin /></Protected>} />
                                <Route path="/profile" element={<Protected><EditProfile /></Protected>} />
                                <Route path="/account/security" element={<Protected><AccountSecurity /></Protected>} />
                                <Route path="/creator/dashboard" element={<Protected><CreatorDashboard /></Protected>} />
                                <Route path="/creator/:ident" element={<CreatorProfile />} />
                                <Route path="/@:ident" element={<CreatorProfile />} />
                                <Route path="/edit-profile" element={<Protected><EditProfile /></Protected>} />
                                <Route path="/login" element={<AuthRedirect mode="signin" />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/register" element={<AuthRedirect mode="signup" />} />
                                <Route path="/privacy" element={<Privacy />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/faq" element={<FAQ />} />
                                <Route path="/delete-account" element={<DeleteAccount />} />
                                <Route path="/legal/:slug" element={<Legal />} />
                                {/* v79.228 — Digital Store playbook moved from
                                    /help/digital-stores → /how-it-works/digital-stores
                                    for consistency with the merged How-it-works
                                    nav. Old path still 301-redirects. */}
                                <Route path="/how-it-works/digital-stores" element={<HelpDigitalStores />} />
                                <Route path="/help/digital-stores" element={<Navigate to="/how-it-works/digital-stores" replace />} />
                                {/* v78.60/.61 — Public catalog hub for sales prospects.
                                    Maps the friendlier /catalogs URL to the
                                    static HTML hub we ship under public/.
                                    Side-effect now lives inside a mounted
                                    component (useEffect) so it does NOT
                                    fire on every App render. */}
                                <Route path="/catalogs" element={<CatalogRedirect />} />
                                {/* v79.228 — Founder direction: "Help and How it
                                    works are the same page — keep How it works,
                                    remove Help." /how-it-works is now the
                                    canonical URL for the User Guide; the
                                    legacy /help path 301-redirects here so
                                    any external bookmarks / email links keep
                                    working. */}
                                <Route path="/how-it-works" element={<UserGuide />} />
                                <Route path="/help" element={<Navigate to="/how-it-works" replace />} />
                                <Route path="/guide" element={<UserGuide />} />
                                {/* v78.59 — Learning Hub. Public for SEO &
                                    AdSense depth-content. */}
                                <Route path="/guides" element={<Guides />} />
                                <Route path="/guides/:slug" element={<GuideDetail />} />
                                <Route path="/employer/apply" element={<Protected><EmployerApply /></Protected>} />
                                <Route path="/employer/shortlist" element={<Protected><EmployerShortlist /></Protected>} />
                                <Route path="/candidates" element={<Protected><Candidates /></Protected>} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </Suspense>
                        </main>
                        <ChromeFooter />
                        <HelperBot />
                        {/* v79.188 — Founder direction (production,
                            Feb 2026): "Remove share link to Adukoo
                            Gang page button on all pages." Global
                            floating FAB permanently removed — users
                            can still share individual listings via
                            the per-card ShareButton and the browser
                            share menu.
                        <FunboardShareFabGlobal /> */}
                        <GlobalMediaPlayer />
                        {/* v79.212 — JumpToTop / Back-to-top FAB removed
                            per founder direction ("Not required."). Reduces
                            floating-widget noise and stops the bot pill
                            from having to fight for vertical safe-area
                            below the HelperBot launcher. */}
                        <Toaster position="top-right" richColors />
                        </ShortlistProvider>
                    </WishlistProvider>
                </AuthProvider>
            </BrowserRouter>
            </ErrorBoundary>
        </div>
    );
}
